/**
 * Live Chat Component
 *
 * YouTube-style live chat component for live events.
 * Uses signal-based DI for state management.
 *
 * Features:
 * - Real-time message display via SSE
 * - Message input with emoji picker (authenticated users)
 * - Login prompt (unauthenticated users)
 * - Loading, error, and empty states
 */

import { html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { SignalElement, inject } from "ui-common/di";
import type { ILiveChatState, ILiveChatService, IChatMessage } from "ui-common/services/live-chat/types";
import type { ISseState } from "ui-common/services/sse/types";
import { IOSKeyboardController } from "ui-common/ios-keyboard-controller";
import { liveChatStyles } from "./live-chat.css";
import "../button/wow-button";
import "../form-elements/textarea/textarea";
import "@phosphor-icons/webcomponents/PhSmiley";
import "@phosphor-icons/webcomponents/PhPaperPlaneTilt";
import "@phosphor-icons/webcomponents/PhHourglass";
import { getAssetUrl } from "helpers/assetHelpers";

const EMOJIS = ["❤️", "🔥", "👏", "😍", "🎉", "💯", "😂", "🙌", "✨", "💪", "👀", "🤩"];

/**
 * Live chat component for live events.
 *
 * @fires show-login - Dispatched when unauthenticated user clicks login (not implemented yet)
 *
 * @example
 * <live-chat event-id="event-123" ?is-authenticated=${true}></live-chat>
 */
@customElement("live-chat")
export class LiveChat extends SignalElement {
  static override styles = [liveChatStyles];

  /**
   * The live event ID to connect to.
   */
  @property({ type: String, attribute: "event-id" })
  eventId = "";

  /**
   * Whether the current user is authenticated.
   */
  @property({ type: Boolean, attribute: "is-authenticated" })
  isAuthenticated = false;

  /**
   * The live event status.
   * When "scheduled", shows a waiting message instead of chat input.
   */
  @property({ type: String, attribute: "event-status" })
  eventStatus: "scheduled" | "live" | "ended" | "cancelled" | "" = "";

  /**
   * Whether the event is free (allows anonymous chat viewing).
   * When true, unauthenticated users can view messages but not send them.
   */
  @property({ type: Boolean, attribute: "is-free-event" })
  isFreeEvent = false;

  /**
   * Injected live chat state.
   * Component re-renders automatically when state signals change.
   */
  @inject("liveChatState")
  chatState!: ILiveChatState;

  /**
   * Injected live chat service.
   * Provides methods to connect, disconnect, and send messages.
   */
  @inject<ILiveChatService>("liveChatService")
  chatService!: ILiveChatService;

  /**
   * Injected SSE state.
   * Used to check connection status for send button state.
   */
  @inject<ISseState>("sseState")
  sseState!: ISseState;

  /**
   * Current message input value.
   */
  @state()
  private inputValue = "";

  /**
   * Whether the emoji picker is visible.
   */
  @state()
  private showEmojiPicker = false;

  /**
   * Track previous message count for auto-scroll detection.
   */
  private previousMessageCount = 0;

  /**
   * Whether the user can view chat messages.
   * True if authenticated OR if the event is free.
   */
  private get canViewChat(): boolean {
    return this.isAuthenticated || this.isFreeEvent;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    if (this.eventId && this.canViewChat) {
      this.chatService?.connect(this.eventId);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.chatService?.disconnect();
  }

  override updated(changedProps: Map<string, unknown>): void {
    super.updated(changedProps);
    const relevantChange =
      changedProps.has("eventId") || changedProps.has("isAuthenticated") || changedProps.has("isFreeEvent");

    if (relevantChange && this.eventId && this.canViewChat) {
      this.chatService?.connect(this.eventId);
    }

    // Auto-scroll to bottom when new messages arrive
    const messages = this.chatState?.messages.get() ?? [];
    if (messages.length > this.previousMessageCount) {
      this.scrollToBottom();
    }
    this.previousMessageCount = messages.length;
  }

  /**
   * Scroll the messages container to the bottom.
   * Uses requestAnimationFrame to ensure scroll happens after render.
   */
  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const container = this.shadowRoot?.querySelector(".messages-container");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    });
  }

  private handleInputChange(e: Event): void {
    const input = e.target as HTMLTextAreaElement;
    this.inputValue = input.value;
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  }

  private async handleSend(): Promise<void> {
    const trimmed = this.inputValue.trim();
    if (!trimmed || !this.chatService) return;

    const success = await this.chatService.sendMessage(trimmed);
    if (success) {
      this.inputValue = "";
      await this.updateComplete;
      const textarea = this.shadowRoot?.querySelector("wow-textarea") as
        | (HTMLElement & { input?: HTMLTextAreaElement })
        | null;
      textarea?.input?.focus();
    }
  }

  private handleEmojiToggle(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  private handleEmojiClick(emoji: string): void {
    this.inputValue += emoji;
    this.showEmojiPicker = false;
    const textarea = this.shadowRoot?.querySelector("wow-textarea") as
      | (HTMLElement & { input?: HTMLTextAreaElement })
      | null;
    textarea?.input?.focus();
  }

  private handleLoginClick(): void {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `/my/sign-in?return=${returnUrl}`;
  }

  private handleRetry(): void {
    if (this.eventId) {
      this.chatService?.connect(this.eventId);
    }
  }

  private handleInputFocus(): void {
    const container = this.shadowRoot?.querySelector(".messages-container");
    IOSKeyboardController.scrollToBottomAfterFocus(container);
  }

  private handleInputBlur(): void {
    const container = this.shadowRoot?.querySelector(".messages-container");
    IOSKeyboardController.fixScrollAfterKeyboardDismiss(container);
  }

  private formatTime(sentAt: string): string {
    try {
      const date = new Date(sentAt);
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  }

  private getInitials(name: string): string {
    return name.charAt(0).toUpperCase();
  }

  private renderLoading(): TemplateResult {
    return html`
      <div class="loading-container">
        <div class="loading-spinner"></div>
      </div>
    `;
  }

  private renderError(): TemplateResult {
    const errorMessage = this.chatState?.errorMessage.get() ?? "Failed to load chat";
    return html`
      <div class="error-container">
        <div class="error-message">${errorMessage}</div>
        <wow-button variant="primary" size="small" @click=${this.handleRetry}>Retry</wow-button>
      </div>
    `;
  }

  private renderEmpty(): TemplateResult {
    return html`
      <div class="empty-container">
        <span>No messages yet. Be the first to say something!</span>
      </div>
    `;
  }

  private renderWaitingForEvent(): TemplateResult {
    return html`
      <div class="waiting-container">
        <ph-hourglass class="waiting-icon" size="32" weight="duotone"></ph-hourglass>
        <p class="waiting-message">Chat will be available when the event starts</p>
      </div>
    `;
  }

  private renderMessage(message: IChatMessage): TemplateResult {
    return html`
      <div class="message" data-message-id=${message.id}>
        <div class="message-avatar">
          ${message.userAvatarUrl
            ? html`<img src=${getAssetUrl(message.userAvatarUrl)} alt=${message.userName} />`
            : html`<div class="avatar-fallback">${this.getInitials(message.userName)}</div>`}
        </div>
        <div class="message-content">
          <div class="message-header">
            <span class="message-author">${message.userName}</span>
            <span class="message-time">${this.formatTime(message.sentAt)}</span>
          </div>
          <div class="message-text">${message.content}</div>
        </div>
      </div>
    `;
  }

  private renderMessages(): TemplateResult {
    const messages = this.chatState?.messages.get() ?? [];
    if (messages.length === 0) {
      return this.renderEmpty();
    }
    return html` <div class="messages-container">${messages.map((m) => this.renderMessage(m))}</div> `;
  }

  private renderLoginPrompt(): TemplateResult {
    return html`
      <div class="login-prompt">
        <span>Sign in to join the conversation</span>
        <wow-button variant="primary" @click=${this.handleLoginClick}>Sign In</wow-button>
      </div>
    `;
  }

  private renderEmojiPicker(): TemplateResult {
    return html`
      <div class="emoji-picker ${this.showEmojiPicker ? "" : "hidden"}">
        ${EMOJIS.map(
          (emoji) => html`
            <wow-button variant="subtle" size="small" @click=${() => this.handleEmojiClick(emoji)}>${emoji}</wow-button>
          `,
        )}
      </div>
    `;
  }

  private renderInput(): TemplateResult {
    const isSending = this.chatState?.isSending.get() ?? false;
    const connectionStatus = this.chatState?.connectionStatus.get() ?? "idle";
    // Note: hasSession check removed - this method is only called when isAuthenticated is true (line 365)
    // and RPC validates auth server-side, so the SSE session check was redundant
    const canSend = this.inputValue.trim().length > 0 && !isSending && connectionStatus === "connected";

    return html`
      <div class="input-container">
        ${this.renderEmojiPicker()}
        <div class="input-wrapper">
          <wow-button ?disabled=${isSending} variant="primary" square @click=${this.handleEmojiToggle}>
            <ph-smiley size="20" weight="bold"></ph-smiley>
          </wow-button>
          <wow-textarea
            class="message-input"
            placeholder="Send a message..."
            .value=${this.inputValue}
            @input=${this.handleInputChange}
            @keydown=${this.handleKeyDown}
            @focus=${this.handleInputFocus}
            @blur=${this.handleInputBlur}
            ?disabled=${isSending}
            rows="1"
            autogrow
          ></wow-textarea>
          <wow-button variant="primary" square @click=${this.handleSend} ?disabled=${!canSend}>
            <ph-paper-plane-tilt size="20" weight="fill"></ph-paper-plane-tilt>
          </wow-button>
        </div>
      </div>
    `;
  }

  override render(): TemplateResult {
    // For scheduled events, show waiting message instead of chat
    if (this.eventStatus === "scheduled") {
      return html`
        <div class="chat-container">
          <div class="chat-header">Live Chat</div>
          ${this.renderWaitingForEvent()}
        </div>
      `;
    }

    const status = this.chatState?.connectionStatus.get() ?? "idle";
    const showInputArea = status !== "loading" && status !== "error";

    return html`
      <div class="chat-container">
        <div class="chat-header">Live Chat</div>

        ${status === "loading" ? this.renderLoading() : status === "error" ? this.renderError() : this.renderMessages()}
        ${showInputArea ? (this.isAuthenticated ? this.renderInput() : this.renderLoginPrompt()) : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-chat": LiveChat;
  }
}
